import { createContext, useContext, useState, useMemo, useCallback } from 'react'
import { useCorpusData } from './CorpusDataContext'

const FilterContext = createContext()

// US Region mapping for geographic grouping
const REGION_MAP = {
  'Northeast': ['CT', 'MA', 'ME', 'NH', 'NJ', 'NY', 'PA', 'RI', 'VT'],
  'Southeast': ['AL', 'AR', 'FL', 'GA', 'KY', 'LA', 'MS', 'NC', 'SC', 'TN', 'VA', 'WV'],
  'Midwest': ['IA', 'IL', 'IN', 'KS', 'MI', 'MN', 'MO', 'ND', 'NE', 'OH', 'SD', 'WI'],
  'Southwest': ['AZ', 'NM', 'OK', 'TX'],
  'West': ['AK', 'CA', 'CO', 'HI', 'ID', 'MT', 'NV', 'OR', 'UT', 'WA', 'WY'],
  'Mid-Atlantic': ['DC', 'DE', 'MD']
}

export function FilterProvider({ children }) {
  // Get case studies from corpus data context (fetched from GitHub)
  const { caseStudies, loading: dataLoading, error: dataError } = useCorpusData()

  // Filter states - Basic
  const [equipmentType, setEquipmentType] = useState('')
  const [industry, setIndustry] = useState('')
  const [state, setState] = useState('')
  const [region, setRegion] = useState('')
  const [refrigerant, setRefrigerant] = useState('')
  const [manufacturer, setManufacturer] = useState('')

  // Filter states - Ranges
  const [ageRange, setAgeRange] = useState([0, 30])
  const [capacityRange, setCapacityRange] = useState([0, 2000])
  const [efficiencyRange, setEfficiencyRange] = useState([0, 100])
  const [savingsRange, setSavingsRange] = useState([0, 500000])
  const [kwTonRange, setKwTonRange] = useState([0, 3])

  // Filter states - Advanced
  const [dataQuality, setDataQuality] = useState('')
  const [category, setCategory] = useState('')
  const [peEngineer, setPeEngineer] = useState('')
  const [dateRange, setDateRange] = useState({ start: '', end: '' })

  // Multi-select filters (for multiple values)
  const [selectedEquipmentTypes, setSelectedEquipmentTypes] = useState([])
  const [selectedIndustries, setSelectedIndustries] = useState([])
  const [selectedRefrigerants, setSelectedRefrigerants] = useState([])
  const [selectedStates, setSelectedStates] = useState([])

  // Sort state
  const [sortField, setSortField] = useState('efficiency_pct')
  const [sortDirection, setSortDirection] = useState('desc')

  // Get unique filter options from data
  const filterOptions = useMemo(() => {
    // Return defaults if data not loaded yet
    if (!caseStudies || caseStudies.length === 0) {
      return {
        equipmentTypes: [],
        industries: [],
        states: [],
        regions: Object.keys(REGION_MAP),
        refrigerants: [],
        manufacturers: [],
        categories: [],
        qualities: [],
        certifiers: [],
        maxEfficiency: 100,
        maxSavings: 500000,
        maxCapacity: 2000,
        maxAge: 30
      }
    }

    const efficiencies = caseStudies.map(c => c.efficiency_pct).filter(Boolean)
    const savings = caseStudies.map(c => c.annual_savings).filter(Boolean)
    const capacities = caseStudies.map(c => c.capacity_tons).filter(Boolean)
    const ages = caseStudies.map(c => c.equipment_age).filter(v => v != null)

    return {
      equipmentTypes: [...new Set(caseStudies.map(c => c.equipment_type).filter(Boolean))].sort(),
      industries: [...new Set(caseStudies.map(c => c.industry).filter(Boolean))].sort(),
      states: [...new Set(caseStudies.map(c => c.state).filter(Boolean))].sort(),
      regions: Object.keys(REGION_MAP),
      refrigerants: [...new Set(caseStudies.map(c => c.refrigerant).filter(Boolean))].sort(),
      manufacturers: [...new Set(caseStudies.map(c => c.manufacturer).filter(Boolean))].sort(),
      categories: [...new Set(caseStudies.map(c => c.category).filter(Boolean))].sort(),
      qualities: [...new Set(caseStudies.map(c => c.quality).filter(Boolean))].sort(),
      certifiers: [...new Set(caseStudies.map(c => c.pe_name).filter(Boolean))].sort(),
      // Computed ranges for sliders
      maxEfficiency: Math.ceil(Math.max(...efficiencies, 100)),
      maxSavings: Math.ceil(Math.max(...savings, 500000)),
      maxCapacity: Math.ceil(Math.max(...capacities, 2000)),
      maxAge: Math.ceil(Math.max(...ages, 30))
    }
  }, [caseStudies])

  // Filtered data with comprehensive filtering
  const filteredData = useMemo(() => {
    // Return empty array if data not loaded yet
    if (!caseStudies || caseStudies.length === 0) return []

    let filtered = caseStudies.filter(study => {
      // Single-select filters
      if (equipmentType && study.equipment_type !== equipmentType) return false
      if (industry && study.industry !== industry) return false
      if (state && study.state !== state) return false
      if (refrigerant && study.refrigerant !== refrigerant) return false
      if (manufacturer && study.manufacturer !== manufacturer) return false
      if (dataQuality && study.quality !== dataQuality) return false
      if (category && study.category !== category) return false
      if (peEngineer && study.pe_name !== peEngineer) return false

      // Region filter (maps to multiple states)
      if (region && study.state) {
        const regionStates = REGION_MAP[region] || []
        if (!regionStates.includes(study.state)) return false
      }

      // Multi-select filters (OR within group)
      if (selectedEquipmentTypes.length > 0 && !selectedEquipmentTypes.includes(study.equipment_type)) return false
      if (selectedIndustries.length > 0 && !selectedIndustries.includes(study.industry)) return false
      if (selectedRefrigerants.length > 0 && !selectedRefrigerants.includes(study.refrigerant)) return false
      if (selectedStates.length > 0 && !selectedStates.includes(study.state)) return false

      // Range filters - Age
      if (study.equipment_age != null) {
        if (study.equipment_age < ageRange[0] || study.equipment_age > ageRange[1]) return false
      }

      // Range filters - Capacity
      if (study.capacity_tons != null) {
        if (study.capacity_tons < capacityRange[0] || study.capacity_tons > capacityRange[1]) return false
      }

      // Range filters - Efficiency
      if (study.efficiency_pct != null && (efficiencyRange[0] > 0 || efficiencyRange[1] < 100)) {
        if (study.efficiency_pct < efficiencyRange[0] || study.efficiency_pct > efficiencyRange[1]) return false
      }

      // Range filters - Savings
      if (study.annual_savings != null && (savingsRange[0] > 0 || savingsRange[1] < 500000)) {
        if (study.annual_savings < savingsRange[0] || study.annual_savings > savingsRange[1]) return false
      }

      // Range filters - kW/Ton before
      if (study.kw_per_ton_before != null && (kwTonRange[0] > 0 || kwTonRange[1] < 3)) {
        if (study.kw_per_ton_before < kwTonRange[0] || study.kw_per_ton_before > kwTonRange[1]) return false
      }

      return true
    })

    // Apply sorting
    filtered.sort((a, b) => {
      const aVal = a[sortField] ?? 0
      const bVal = b[sortField] ?? 0
      return sortDirection === 'desc' ? bVal - aVal : aVal - bVal
    })

    return filtered
  }, [
    caseStudies,
    equipmentType, industry, state, region, refrigerant, manufacturer,
    dataQuality, category, peEngineer,
    selectedEquipmentTypes, selectedIndustries, selectedRefrigerants, selectedStates,
    ageRange, capacityRange, efficiencyRange, savingsRange, kwTonRange,
    sortField, sortDirection
  ])

  // Aggregated stats from filtered data - comprehensive
  const filteredStats = useMemo(() => {
    const withEfficiency = filteredData.filter(d => d.efficiency_pct)
    const withSavings = filteredData.filter(d => d.annual_savings)
    const withKwTon = filteredData.filter(d => d.kw_per_ton_before && d.kw_per_ton_after)
    const withCapacity = filteredData.filter(d => d.capacity_tons)
    const withAge = filteredData.filter(d => d.equipment_age != null)

    // Efficiency stats
    const efficiencies = withEfficiency.map(d => d.efficiency_pct)
    const avgEfficiency = efficiencies.length > 0
      ? efficiencies.reduce((a, b) => a + b, 0) / efficiencies.length : 0
    const minEfficiency = efficiencies.length > 0 ? Math.min(...efficiencies) : 0
    const maxEfficiency = efficiencies.length > 0 ? Math.max(...efficiencies) : 0

    // Savings stats
    const savings = withSavings.map(d => d.annual_savings)
    const totalSavings = savings.reduce((a, b) => a + b, 0)
    const avgSavings = savings.length > 0 ? totalSavings / savings.length : 0

    // kW/Ton stats
    const kwTonBefore = withKwTon.map(d => d.kw_per_ton_before)
    const kwTonAfter = withKwTon.map(d => d.kw_per_ton_after)
    const avgKwTonBefore = kwTonBefore.length > 0
      ? kwTonBefore.reduce((a, b) => a + b, 0) / kwTonBefore.length : 0
    const avgKwTonAfter = kwTonAfter.length > 0
      ? kwTonAfter.reduce((a, b) => a + b, 0) / kwTonAfter.length : 0
    const avgKwTonReduction = avgKwTonBefore > 0
      ? ((avgKwTonBefore - avgKwTonAfter) / avgKwTonBefore * 100) : 0

    // Capacity stats
    const capacities = withCapacity.map(d => d.capacity_tons)
    const totalCapacity = capacities.reduce((a, b) => a + b, 0)
    const avgCapacity = capacities.length > 0 ? totalCapacity / capacities.length : 0

    // Age stats
    const ages = withAge.map(d => d.equipment_age)
    const avgAge = ages.length > 0 ? ages.reduce((a, b) => a + b, 0) / ages.length : 0

    // Breakdowns by category
    const byEquipment = {}
    const byIndustry = {}
    const byRefrigerant = {}
    const byRegion = {}

    filteredData.forEach(d => {
      if (d.equipment_type) byEquipment[d.equipment_type] = (byEquipment[d.equipment_type] || 0) + 1
      if (d.industry) byIndustry[d.industry] = (byIndustry[d.industry] || 0) + 1
      if (d.refrigerant) byRefrigerant[d.refrigerant] = (byRefrigerant[d.refrigerant] || 0) + 1
      if (d.state) {
        const studyRegion = Object.entries(REGION_MAP).find(([_, states]) => states.includes(d.state))?.[0]
        if (studyRegion) byRegion[studyRegion] = (byRegion[studyRegion] || 0) + 1
      }
    })

    return {
      count: filteredData.length,
      totalStudies: caseStudies?.length || 0,
      // Efficiency
      avgEfficiency, minEfficiency, maxEfficiency,
      // Savings
      totalSavings, avgSavings,
      // kW/Ton
      avgKwTonBefore, avgKwTonAfter, avgKwTonReduction,
      // Capacity & Age
      totalCapacity, avgCapacity, avgAge,
      // Breakdowns
      byEquipment, byIndustry, byRefrigerant, byRegion
    }
  }, [filteredData])

  const clearFilters = useCallback(() => {
    setEquipmentType('')
    setIndustry('')
    setState('')
    setRegion('')
    setRefrigerant('')
    setManufacturer('')
    setDataQuality('')
    setCategory('')
    setPeEngineer('')
    setAgeRange([0, 30])
    setCapacityRange([0, 2000])
    setEfficiencyRange([0, 100])
    setSavingsRange([0, 500000])
    setKwTonRange([0, 3])
    setSelectedEquipmentTypes([])
    setSelectedIndustries([])
    setSelectedRefrigerants([])
    setSelectedStates([])
    setDateRange({ start: '', end: '' })
  }, [])

  // Apply preset filters
  const applyPreset = useCallback((filters) => {
    clearFilters()
    Object.entries(filters).forEach(([key, value]) => {
      switch (key) {
        case 'equipmentType': setEquipmentType(value); break
        case 'industry': setIndustry(value); break
        case 'state': setState(value); break
        case 'region': setRegion(value); break
        case 'refrigerant': setRefrigerant(value); break
        case 'manufacturer': setManufacturer(value); break
        case 'dataQuality': setDataQuality(value); break
        case 'category': setCategory(value); break
        case 'peEngineer': setPeEngineer(value); break
        case 'ageRange': setAgeRange(value); break
        case 'capacityRange': setCapacityRange(value); break
        case 'efficiencyRange': setEfficiencyRange(value); break
        case 'savingsRange': setSavingsRange(value); break
        case 'kwTonRange': setKwTonRange(value); break
        case 'selectedEquipmentTypes': setSelectedEquipmentTypes(value); break
        case 'selectedIndustries': setSelectedIndustries(value); break
        case 'selectedRefrigerants': setSelectedRefrigerants(value); break
        case 'selectedStates': setSelectedStates(value); break
      }
    })
  }, [clearFilters])

  // Get current filters as object (for saving presets)
  const getCurrentFilters = useCallback(() => ({
    equipmentType, industry, state, region, refrigerant, manufacturer,
    dataQuality, category, peEngineer,
    ageRange, capacityRange, efficiencyRange, savingsRange, kwTonRange,
    selectedEquipmentTypes, selectedIndustries, selectedRefrigerants, selectedStates
  }), [
    equipmentType, industry, state, region, refrigerant, manufacturer,
    dataQuality, category, peEngineer,
    ageRange, capacityRange, efficiencyRange, savingsRange, kwTonRange,
    selectedEquipmentTypes, selectedIndustries, selectedRefrigerants, selectedStates
  ])

  const activeFilterCount = [
    equipmentType,
    industry,
    state,
    region,
    refrigerant,
    manufacturer,
    dataQuality,
    category,
    peEngineer,
    selectedEquipmentTypes.length > 0,
    selectedIndustries.length > 0,
    selectedRefrigerants.length > 0,
    selectedStates.length > 0,
    ageRange[0] > 0 || ageRange[1] < 30,
    capacityRange[0] > 0 || capacityRange[1] < 2000,
    efficiencyRange[0] > 0 || efficiencyRange[1] < 100,
    savingsRange[0] > 0 || savingsRange[1] < 500000,
    kwTonRange[0] > 0 || kwTonRange[1] < 3
  ].filter(Boolean).length

  return (
    <FilterContext.Provider value={{
      // Loading state (from corpus data fetch)
      dataLoading, dataError,
      // Filter values - Basic
      equipmentType, industry, state, region, refrigerant, manufacturer,
      // Filter values - Advanced
      dataQuality, category, peEngineer, dateRange,
      // Filter values - Ranges
      ageRange, capacityRange, efficiencyRange, savingsRange, kwTonRange,
      // Filter values - Multi-select
      selectedEquipmentTypes, selectedIndustries, selectedRefrigerants, selectedStates,
      // Sort
      sortField, sortDirection,
      // Setters - Basic
      setEquipmentType, setIndustry, setState, setRegion, setRefrigerant, setManufacturer,
      // Setters - Advanced
      setDataQuality, setCategory, setPeEngineer, setDateRange,
      // Setters - Ranges
      setAgeRange, setCapacityRange, setEfficiencyRange, setSavingsRange, setKwTonRange,
      // Setters - Multi-select
      setSelectedEquipmentTypes, setSelectedIndustries, setSelectedRefrigerants, setSelectedStates,
      // Setters - Sort
      setSortField, setSortDirection,
      // Data
      filterOptions, filteredData, filteredStats,
      // Helpers
      clearFilters, applyPreset, getCurrentFilters, activeFilterCount,
      // Constants
      REGION_MAP
    }}>
      {children}
    </FilterContext.Provider>
  )
}

export function useFilters() {
  const context = useContext(FilterContext)
  if (!context) {
    throw new Error('useFilters must be used within a FilterProvider')
  }
  return context
}

export { REGION_MAP }
export default FilterContext
